import { useEffect, useRef, type RefObject } from 'react';
import { lockBodyScroll, unlockBodyScroll } from './scroll-lock';

/**
 * Fælles tilgængelighed for dialoger, modaler, skuffer og sheets:
 * Escape (kun øverste dialog), fokus-trap, fokus-retur, scroll-lock og startfokus.
 * Kald hooket i komponenten og sæt `ref` på dialog-containeren (som også bør have role="dialog",
 * aria-modal og aria-labelledby/aria-label).
 */
export interface UseDialogA11yOptions {
  /** Dialogen er åben. Standard: true (komponenter der kun monteres når de er åbne). */
  open?: boolean;
  /** Kaldes ved Escape. Udelades for samtykke-dialoger, der ikke må lukkes med Escape. */
  onClose?: () => void;
  /** Slå Escape fra midlertidigt (fx under indsendelse). Standard: true. */
  closeOnEscape?: boolean;
  /** Hold Tab/Shift+Tab inden for dialogen. Standard: true. Slå fra for guider, der peger på siden under. */
  trapFocus?: boolean;
  /** Lås baggrundens scroll. Standard: true. */
  lockScroll?: boolean;
  /** Returnér fokus til elementet, der havde det før åbning. Standard: true. */
  restoreFocus?: boolean;
  /** Eksisterende ref, hvis komponenten selv skal bruge containeren. */
  ref?: RefObject<HTMLElement | null>;
}

export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

/** Beregner næste fokusindeks ved Tab-trap. Returnerer null når browseren selv må håndtere Tab. */
export function nextTrapIndex(current: number, count: number, shift: boolean): number | null {
  if (count === 0) return -1;
  if (current === -1) return shift ? count - 1 : 0;
  if (shift && current === 0) return count - 1;
  if (!shift && current === count - 1) return 0;
  return null;
}

const dialogStack: symbol[] = [];
function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) =>
      !el.hasAttribute('hidden') && el.getAttribute('aria-hidden') !== 'true' && el.tabIndex >= 0,
  );
}

export function useDialogA11y<T extends HTMLElement = HTMLDivElement>(
  options: UseDialogA11yOptions = {},
): { ref: RefObject<T | null> } {
  const {
    open = true,
    closeOnEscape = true,
    trapFocus = true,
    lockScroll = true,
    restoreFocus = true,
  } = options;
  const ownRef = useRef<T | null>(null);
  const ref = (options.ref as RefObject<T | null> | undefined) ?? ownRef;
  const onCloseRef = useRef(options.onClose);
  onCloseRef.current = options.onClose;
  const closeOnEscapeRef = useRef(closeOnEscape);
  closeOnEscapeRef.current = closeOnEscape;

  useEffect(() => {
    if (!open) return;
    const token = Symbol('dialog');
    dialogStack.push(token);
    const previouslyFocused = document.activeElement as HTMLElement | null;
    if (lockScroll) lockBodyScroll();

    const container = ref.current;
    if (container && !container.contains(document.activeElement)) {
      const preferred = container.querySelector<HTMLElement>('[data-autofocus]');
      const target = preferred ?? (trapFocus ? getFocusable(container)[0] : undefined);
      if (target) target.focus({ preventScroll: true });
      else if (trapFocus) {
        if (!container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
        container.focus({ preventScroll: true });
      }
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (dialogStack[dialogStack.length - 1] !== token) return;
      if (event.key === 'Escape') {
        if (closeOnEscapeRef.current && onCloseRef.current) {
          event.preventDefault();
          event.stopPropagation();
          onCloseRef.current();
        }
        return;
      }
      if (event.key !== 'Tab' || !trapFocus || !ref.current) return;
      const items = getFocusable(ref.current);
      const current = items.indexOf(document.activeElement as HTMLElement);
      const next = nextTrapIndex(current, items.length, event.shiftKey);
      if (next === null) return;
      event.preventDefault();
      if (next === -1) ref.current.focus({ preventScroll: true });
      else items[next].focus({ preventScroll: true });
    };
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      const index = dialogStack.indexOf(token);
      if (index !== -1) dialogStack.splice(index, 1);
      if (lockScroll) unlockBodyScroll();
      if (restoreFocus && previouslyFocused && previouslyFocused.isConnected) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [open, trapFocus, lockScroll, restoreFocus]);

  return { ref };
}
